import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';

import {Button} from '@/components';
import {fileService, kycBucketKey} from '@/services/fileService';
import type {AfriexVirtualAccountApplicationData} from '@/types';
import {capitalizeWords} from '@/utils/dashboard';
import * as formatDate from '@/utils/date';
import {toSafeExternalUrl} from '@/utils/externalUrl';
import {renderFile} from '@/utils/imageFiles';

interface VirtualAccountApplicationProps {
  data: AfriexVirtualAccountApplicationData;
}

const VirtualAccountApplication = ({
  data,
}: VirtualAccountApplicationProps): ReactElement => {
  // Applicant-supplied, so it is only opened when it is a plain http(s) link.
  const linkedInProfileUrl = toSafeExternalUrl(
    data?.linkedInProfile?.toLocaleLowerCase(),
  );
  const docName =
    data?.nameOfDocument && data?.nameOfDocument?.length > 0
      ? data?.nameOfDocument
      : 'proof_of_income';
  const [url, setUrl] = useState<string>();

  useEffect(() => {
    const doc =
      data?.document && data?.document?.length > 'VirtualAccount/'.length
        ? data?.document
        : '';
    const getDocumentUrl = async () => {
      const getUrl = await fileService.fetchUploadedFile(doc, kycBucketKey);
      setUrl(getUrl);
    };

    // getDocumentUrl has no try/catch of its own, so this is the only place a
    // failed lookup can be handled. It stays silent to the admin (the
    // document block simply does not render, as today) and matches how the
    // sibling KYCRequestedDocuments logs a failed file fetch.
    if (doc?.length) {
      getDocumentUrl().catch((error) => {
        console.error('Failed to fetch the virtual account document:', error);
      });
    }
  }, [data?.document]);

  return (
    <div>
      {url && (
        <div className="mb-2 py-2 flex items-center gap-3 justify-between">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-200 flex flex-col gap-1">
            {renderFile(url, docName, 'w-40')}
            <span className="flex items-center gap-2">
              {capitalizeWords(docName.replace(/_/g, ' '))}
              <Button
                onClick={() => window.open(url, '_blank')}
                variant="ghost"
                className="no-padding no-margin"
              >
                View Image
              </Button>
            </span>
          </p>
        </div>
      )}

      {/* Application Date */}
      {data?.createdAt && (
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="text-slate-500 flex-[0.4] text-sm dark:text-slate-100">
            Application Date
          </p>
          <p className="text-sm font-medium flex-[0.6] text-gray-700 w-1/2 dark:text-gray-100">
            {formatDate.DDMonYYYY(data.createdAt?.toString() || '')}
          </p>
        </div>
      )}

      {/* Monthly Income */}
      {data?.monthlyIncome && (
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="text-slate-500 flex-[0.4] text-sm dark:text-slate-100">
            Monthly Income
          </p>
          <p className="text-sm font-medium flex-[0.6] text-gray-700 w-1/2 dark:text-gray-100">
            {data.monthlyIncome} USD
          </p>
        </div>
      )}

      {/* Employment Status */}
      {data?.employmentStatus && (
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="text-slate-500 flex-[0.4] text-sm dark:text-slate-100">
            Employment Status
          </p>
          <p className="text-sm font-medium flex-[0.6] text-gray-700 w-1/2 dark:text-gray-100">
            {capitalizeWords(data.employmentStatus.replace(/-/g, ' ') || '')}
          </p>
        </div>
      )}

      {/* SSN */}
      {data?.ssn && (
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="text-slate-500 text-sm flex-[0.4] dark:text-slate-100">
            SSN
          </p>
          <p className="text-sm font-medium flex-[0.6] text-gray-700 w-1/2 dark:text-gray-100">
            {data.ssn}
          </p>
        </div>
      )}

      {/* LinkedIn */}
      {linkedInProfileUrl && (
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="text-slate-500 text-sm flex-[0.4] dark:text-slate-100">
            LinkedIn
          </p>
          <p className="text-sm font-medium flex-[0.6] text-gray-700 w-1/2 dark:text-gray-100">
            <Button
              onClick={() =>
                window.open(linkedInProfileUrl, '_blank', 'noopener,noreferrer')
              }
              variant="ghost"
              className="no-padding no-margin"
            >
              View Profile
            </Button>
          </p>
        </div>
      )}

      {/* Status */}
      {data?.status && (
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="text-slate-500 text-sm flex-[0.4] dark:text-slate-100">
            Status
          </p>
          <p className="text-sm font-medium flex-[0.6] text-gray-700 w-1/2 dark:text-gray-100">
            {capitalizeWords(data?.status)}
          </p>
        </div>
      )}

      {/* Upated */}
      {data?.createdAt && (
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="text-slate-500 text-sm flex-[0.4] dark:text-slate-100">
            Updated On
          </p>
          <p className="text-sm font-medium flex-[0.6] text-gray-700 w-1/2 dark:text-gray-100">
            {formatDate.DDMonYYYY(data.updatedAt?.toString() || '')}
          </p>
        </div>
      )}
    </div>
  );
};

export default VirtualAccountApplication;
